/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { MouseEvent, useState } from 'react';

import {
  AnchorTarget,
  Animation,
  Avatar,
  FloatingBox,
  useClickOutside,
  Typography,
} from '@zapier/design-system';
import { Tooltip } from '@zapier/design-system-beta';
import { labelStyles } from '@zapier/style-encapsulation';
import { createUniqueClassName } from '../../utils/createUniqueClassName';

import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../../constants';
import type { UniversalNavLinkType } from '../../constants';

type Account = {
  id: number;
  name: string;
  imageUrl?: string;
  accountSwitcherUrl: string;
  planName?: string;
};

export type Props = {
  /**
   * List of user's accounts to render in the accounts menu.
   */
  accounts: Array<Account>;
  /**
   * Id of currently active user account.
   */
  currentAccountId?: number;
  /**
   * Email address of the currently logged in user.
   */
  email: string;
  /**
   * Changes the background color to white (#fff).
   */
  hasWhiteBackground?: boolean;
  /**
   * Label describing the accounts menu.
   */
  menuLabel?: string;
  /**
   * Array of links to render in the accounts menu.
   */
  menuLinks?: Array<UniversalNavLinkType>;
  /**
   * Handler called when an account menu item is clicked.
   */
  onAccountClick?: (account: Account, event: MouseEvent) => void;
  /**
   * How large the menu renders, especially the `Avatar`.
   */
  size?: 'small' | 'medium';
};

const detailsClassName = createUniqueClassName('AccountsMenu', 'details');

const MENU_ITEM_HEIGHT = '48px';

const Styles = labelStyles('AccountsMenu', {
  root: {
    position: 'relative',
  },
  accounts: {
    // MENU_ITEM_HEIGHT is height of each account item in the list.
    maxHeight: `calc(${MENU_ITEM_HEIGHT} * 3.5)`,
    overflowY: 'auto',
    // !important to override UnviersalNavLink styles
    '& > a': {
      padding: 'var(--zds-space-4, 4px) var(--zds-space-8, 8px) !important',
    },
  },
  summary: {
    position: 'relative',
    display: 'block',
    cursor: 'pointer',
    transition: Animation.transitionValue,
    '&::-webkit-details-marker': {
      display: 'none',
    },
  },
  divider: {
    margin: 'var(--zds-space-8, 8px) 0',
    height: 1,
    backgroundColor: 'var(--zds-stroke-weaker, #D7D3C9)',
    border: 'none',
  },
  menuContainer: {
    padding: 'var(--zds-space-8, 8px)',
    width: 278,
  },
  email: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 var(--zds-space-8, 8px)',
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-text-weaker, #574E4C)',
    marginBottom: 'var(--zds-space-8, 8px)',
  },
  emailText: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

// Renders a list of accounts as menu items
const renderAccountSwitchMenu = (
  accounts: Props['accounts'],
  currentAccountId: Props['currentAccountId'],
  onAccountClick: Props['onAccountClick'],
  setIsMenuOpen: (isMenuOpen: boolean) => void
) => {
  return (
    <div css={Styles.accounts} data-zds>
      {accounts.map((account: Account) => {
        const isCurrentAccount = account.id === currentAccountId;
        return (
          <UniversalNavLink
            isV3Link={true}
            href={account.accountSwitcherUrl}
            isSelected={isCurrentAccount}
            key={account.id}
            onClick={(event) => {
              if (onAccountClick) {
                onAccountClick(account, event);
              }
              setIsMenuOpen(false);
            }}
            renderBeforeText={() => (
              <div css={Styles.avatar} data-zds>
                <Avatar
                  isBlock={true}
                  isSelected={false}
                  name={account.name}
                  shouldLazilyLoad={false}
                  size={40}
                  url={account.imageUrl}
                />
              </div>
            )}
            role="menuitem"
            text={account.name}
            secondaryText={account.planName}
            showCheckmarkOnSelected={true}
          />
        );
      })}
    </div>
  );
};

const renderMenuLinks = (
  menuLinks: NonNullable<Props['menuLinks']>,
  setIsMenuOpen: (isMenuOpen: boolean) => void
) => {
  return menuLinks.map((menuLink) => (
    <UniversalNavLink
      isV3Link={true}
      href={menuLink.href}
      icon={
        typeof menuLink.icon === 'object'
          ? menuLink.icon.selected
          : menuLink.icon
      }
      key={menuLink.text}
      onClick={() => setIsMenuOpen(false)}
      role="menuitem"
      text={menuLink.text}
      target={menuLink.target as AnchorTarget}
    />
  ));
};

// This is fake placeholder account data for when no current account
// is found, which should only happen when loading accounts. This fallback
// account allows `AccountsMenu` to fully render so the UI is more
// incrementally updated instead of pieces of it conditionally rendering.
const fallbackAccount: Account = {
  id: -1,
  name: '⋯',
  accountSwitcherUrl: '/',
};

/**
 * @private
 * Renders a menu with accounts and account action links.
 */
const AccountsMenu = ({
  accounts,
  email,
  currentAccountId = undefined,
  hasWhiteBackground = undefined,
  menuLabel = 'Accounts Settings',
  menuLinks = AccountsMenuLinks,
  onAccountClick = undefined,
  size = 'medium',
}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => setIsMenuOpen(false),
  });

  const currentAccount: Account =
    accounts.find((account) => account.id === currentAccountId) ||
    fallbackAccount;

  const toggleMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  // The native implementation of details handles keyboard events by default.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div css={Styles.root} data-zds>
      <details
        className={detailsClassName}
        data-testid="accountsMenu"
        open={isMenuOpen}
        ref={(node) => setClickOutsideNode(node)}
        data-zds
      >
        <Tooltip content={currentAccount.name} placement="left">
          <Tooltip.FocusableTrigger
            aria-haspopup="menu"
            aria-label={menuLabel}
            css={Styles.summary}
            data-testid="accounts-menu-summary"
            onClick={toggleMenu}
            tag="summary"
          >
            <Avatar
              isBlock={true}
              isInteractive={true}
              isSelected={isMenuOpen}
              name={currentAccount.name}
              shouldLazilyLoad={false}
              size={size === 'small' ? 25 : 40}
              url={currentAccount.imageUrl}
            />
          </Tooltip.FocusableTrigger>
        </Tooltip>
        <FloatingBox
          align="right"
          hasWhiteBackground={hasWhiteBackground}
          position="south"
          borderRadius={4}
        >
          <div css={Styles.menuContainer} role="menu" data-zds>
            <div css={Styles.email} data-zds>
              <span css={Styles.emailText} data-zds>
                {email}
              </span>
            </div>
            {renderAccountSwitchMenu(
              accounts,
              currentAccountId,
              onAccountClick,
              setIsMenuOpen
            )}
            <hr css={Styles.divider} />
            {renderMenuLinks(menuLinks, setIsMenuOpen)}
          </div>
        </FloatingBox>
      </details>
    </div>
  );
};

export default AccountsMenu;
